import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { ComponentType } from '@angular/cdk/portal';
import {
  CompilerFactory,
  inject,
  Injectable,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { DialogComponent } from './dialog.component';
import { isPlatformBrowser } from '@angular/common';
import { Overlay } from '@angular/cdk/overlay';
import { SideDialogComponent } from './side-dialog/side-dialog.component';

export type DialogProps = {
  component?: any;
  componentProps?: any;
  hasBackdrop?: boolean;
  width?: string;
  label?: string;
  description?: string;
  disableLayout?: boolean;
};

export type DialogDismissProps = {
  data: any;
  role: string | undefined;
};

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private modal: DialogRef | undefined;
  private overlay = inject(Overlay);
  private id = signal<number>(0);
  private ids = signal<string[]>([]);
  private readonly platformId = inject(PLATFORM_ID);

  constructor(private dialog: Dialog) {}

  show(props: DialogProps): DialogRef {
    return this.create(DialogComponent, props);
  }

  showSide(props: DialogProps): DialogRef {
    return this.createSide(SideDialogComponent, props);
  }

  private create(
    component: ComponentType<any>,
    data: DialogProps,
  ): DialogRef | any {
    if (isPlatformBrowser(this.platformId)) {
      this.id.set(this.id() + 1);

      this.modal = this.dialog.open(component, {
        data,
        id: `modal-${this.id()}`,
        hasBackdrop: false,
        autoFocus: false,
      });

      this.ids().push(this.modal.id);

      return this.modal;
    }
  }

  private createSide(
    component: ComponentType<any>,
    data: DialogProps,
  ): DialogRef | any {
    if (isPlatformBrowser(this.platformId)) {
      this.id.set(this.id() + 1);

      const positionStrategy = this.overlay.position().global().right('0px');

      this.modal = this.dialog.open(component, {
        data,
        id: `modal-${this.id()}`,
        panelClass: 'flex-1',
        positionStrategy,
        autoFocus: false,
      });

      this.ids().push(this.modal.id);

      return this.modal;
    }
  }

  dismiss(data?: any, role?: string): void {
    const top = this.getTop();
    const element = this.getElement();
    if (element) {
      const closeAnimation = element.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: 200,
        easing: 'ease-in-out',
      });
      closeAnimation!.onfinish = () => {
        const result: DialogDismissProps = { data: undefined, role: undefined };
        if (data) result.data = data;
        if (role) result.role = role;
        if (top) top.close(result);
        this.ids().pop();
      };
    }
  }

  sideDismiss(data?: any, role?: string): void {
    const top = this.getTop();
    const element = this.getElement();
    if (element) {
      // const closeAnimation = element.animate([{ opacity: 1 }, { opacity: 0 }], {
      //   duration: 200,
      //   easing: 'ease-in-out',
      // });
      // closeAnimation!.onfinish = () => {
      const result: DialogDismissProps = { data: undefined, role: undefined };
      if (data) result.data = data;
      if (role) result.role = role;
      if (top) top.close(result);
      this.ids().pop();
      // };
    }
  }

  getTop() {
    if (this.ids().length > 0) {
      const topId = this.ids()[this.ids().length - 1];
      return this.dialog.getDialogById(topId);
    }

    const dialogs = document.querySelectorAll('cdk-dialog-container');
    const topId = dialogs.item(dialogs.length - 1)?.id;
    if (topId) {
      return this.dialog.getDialogById(topId);
    }

    return null;
  }

  getElement(): HTMLElement | null {
    const dialogs = document.querySelectorAll('cdk-dialog-container');
    return (dialogs.item(dialogs.length - 1) as HTMLElement) || null;
  }
}
